import type { ManufacturerName, SensorType } from "../../../../shared.js";
import { DirectSubChannel, SensorPortCommunicationMode } from "../../../portConfigs.js";
import { SensorClusterConfig, SensorElementOutput } from "../types.js";

// ─── Liquid tank level sensors (ADC, volts -> metres of head) ───────────────
//
// All wire to a single ADC_5V sub-channel and share the liquidTankLevel element's
// linearWithMinAndMax conversion. Neither the AST400 family nor the TE Connectivity sensor has
// any cluster-level configurable parameters — the slope/offset come from the legacy
// per-sensor-type calibration and are baked into the element output.

// PSI -> metres of water column. The AST400 family is spec'd as "5V = full-scale PSI rating", so
// every variant's slope is this same constant scaled by its PSI rating over the 5V range — see the
// legacy `waterLevelSensorAST400_*` conversions (SensorConfiguration.ts), which each independently
// hardcode PSI_TO_METERS_OF_WATER * ratingPsi to varying precision.
const PSI_TO_METERS_OF_WATER = 0.70324961475;

type LinearAdcTankLevelClusterParams = {
    slopeMetresPerVolt: number;
    offsetMetres: number;
    manufacturerName: ManufacturerName;
    modelName: string;
    friendlyName: string;
    legacySensorType: SensorType;
};

function linearAdcTankLevelCluster({
    slopeMetresPerVolt,
    offsetMetres,
    manufacturerName,
    modelName,
    friendlyName,
    legacySensorType,
}: LinearAdcTankLevelClusterParams): SensorClusterConfig<Record<string, never>> {
    const sensorElements: SensorElementOutput<"liquidTankLevel">[] = [
        {
            sensorElementConfigKey: "liquidTankLevel",
            parameters: { slopeMetresPerVolt, offsetMetres },
            legalConvertedRange: [0, null],
            subChannel: DirectSubChannel.ADC_5V,
        },
    ];

    return {
        communicationMode: SensorPortCommunicationMode.DIRECT,
        requiredSubChannels: [DirectSubChannel.ADC_5V],
        optionalSubChannels: [],
        isPermanentlyAttached: false,
        parameterConfigs: {},
        manufacturerName,
        modelName,
        friendlyName,
        friendlyShortName: "Tank Level",
        generateSensorElements: () => sensorElements,
        migrationProperties: {
            legacySensorType,
            legacyParametersToClusterParameters: () => ({}),
            clusterParametersToLegacyParameters: () => ({}),
        },
    };
}

// Legacy `waterLevelSensorAST400_{psiRating}` (SensorConfiguration.ts): 5V = psiRating PSI.
function ast400Cluster(psiRating: number, legacySensorType: SensorType): SensorClusterConfig<Record<string, never>> {
    return linearAdcTankLevelCluster({
        slopeMetresPerVolt: (psiRating * PSI_TO_METERS_OF_WATER) / 5,
        offsetMetres: 0,
        manufacturerName: "Paremtech",
        modelName: `AST400-${psiRating}`,
        friendlyName: `AST400-${psiRating} Tank Level Sensor`,
        legacySensorType,
    });
}

export const liquidLevelClusterConfigs: Record<string, SensorClusterConfig<Record<string, never>>> = {
    AST400_5: ast400Cluster(5, "waterLevelSensorAST400_5"),
    AST400_10: ast400Cluster(10, "waterLevelSensorAST400_10"),
    AST400_40: ast400Cluster(40, "waterLevelSensorAST400_40"),

    // Legacy `tankLevelSensorTEConnectivityUS331` (SensorConfiguration.ts):
    //   0.5V = 0 m, 4.5V = 10.546 m — metres = 2.6365 * volts - 1.31825, clamped to 0 below 0.5V
    TEConnectivity_US331: linearAdcTankLevelCluster({
        slopeMetresPerVolt: 2.6365,
        offsetMetres: -1.31825,
        manufacturerName: "TE Connectivity",
        modelName: "US331-000005-015PG",
        friendlyName: "0-15 PSI Tank Level Sensor",
        legacySensorType: "tankLevelSensorTEConnectivityUS331",
    }),
};
